import React, { useCallback, useState } from 'react';
import { queryCache } from 'react-query';
import { GradientTextAction, InputLabel, Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { ConsequencesSelect, EventsSelect, SourcesSelect } from 'src/components/select';
import { ScenarioCSV, ValidationErrorItem } from './types';
import {
  chunkArray,
  generateCsvTemplateLink,
  mapConsequenceToNumber,
  mapEventToNumber,
  mapSourceToNumber,
} from './util';
import moment from 'moment';
import { DATE_FORMAT_DATE } from 'src/config';
import { removeCommas, selectUserOption } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { ScenarioCsvHeaders } from 'src/api/types/misc';
import { Dropzone, DzoneWrap, ErrorItem, ErrorLog } from 'src/common/dropzone';
import { useCache } from 'src/graphql';
import { ScenarioInput, useUpsertScenarioMutation, useUpsertScenariosMutation } from 'src/graphql/_generated';
import { convertToNumber, delimiters } from 'src/utils/csv';

const scenarioSchema = Yup.object({
  [ScenarioCsvHeaders.Name]: Yup.string().required('Name field is required').typeError('Name field is required'),
  [ScenarioCsvHeaders.Status]: Yup.string().nullable(),
  [ScenarioCsvHeaders.ID]: Yup.string().nullable(),
  [ScenarioCsvHeaders.Sources]: Yup.number().nullable(),
  [ScenarioCsvHeaders.Events]: Yup.number().nullable(),
  [ScenarioCsvHeaders.Consequences]: Yup.number().nullable(),
  [ScenarioCsvHeaders.RiskOwner]: Yup.string().nullable(),
  [ScenarioCsvHeaders.RiskNarrative]: Yup.string().nullable(),
  [ScenarioCsvHeaders.UnmodifiedFrequencyTimes]: Yup.number()
    .nullable()
    .typeError('Should be a number')
    .min(0.001, 'Must be greater than 0'),
  [ScenarioCsvHeaders.UnmodifiedFrequencyYears]: Yup.number()
    .nullable()
    .typeError('Should be a number')
    .min(0.001, 'Must be greater than 0'),
  [ScenarioCsvHeaders.FrequencyNote]: Yup.string().nullable(),
  [ScenarioCsvHeaders.UnmodifiedConsequenceLower]: Yup.string().nullable(),
  [ScenarioCsvHeaders.UnmodifiedConsequenceUpper]: Yup.string().nullable(),
  [ScenarioCsvHeaders.ConsequenceNote]: Yup.string().nullable(),
  [ScenarioCsvHeaders.IdentifiedDate]: Yup.string()
    .nullable()
    .test(ScenarioCsvHeaders.IdentifiedDate, 'Invalid Date', (date) => {
      if (!date) return true;
      return moment(date, DATE_FORMAT_DATE).isValid();
    }),
  [ScenarioCsvHeaders.ReviewDate]: Yup.string()
    .nullable()
    .test(ScenarioCsvHeaders.ReviewDate, 'Invalid Date', (date) => {
      if (!date) return true;
      return moment(date, DATE_FORMAT_DATE).isValid();
    }),
  [ScenarioCsvHeaders.RiskManagementStrategy]: Yup.string().nullable(),
  [ScenarioCsvHeaders.RiskManagementNote]: Yup.string().nullable(),
});

interface ParseResult {
  data: any[];
  errors: any[];
  meta: {
    delimiter: string;
    linebreak: string;
    aborted: boolean;
    truncated: boolean;
    cursor: number;
  };
}

interface FromCsvProps {
  assessmentId: string;
  onClose: () => void;
  setIsScvDirty: (isDirty: boolean) => void;
  state: {
    file: File | null;
    setFile: (file: File | null) => void;
    readyForUpload: boolean;
    setReadyForUpload: (ready: boolean) => void;
  };
}

export const FromCsv: React.FC<FromCsvProps> = ({ assessmentId, onClose, setIsScvDirty, state }) => {
  const { usersOptios } = useAuth();
  const [error, setError] = useState('');
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [validScenarios, setValidScenarios] = useState<ScenarioCSV[]>([]);
  const { refetchQuery } = useCache();

  const { file, setFile, readyForUpload, setReadyForUpload } = state;

  const csvTemplateLink = generateCsvTemplateLink();

  const [upsertScenarios, { loading }] = useUpsertScenariosMutation();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    mpEvent(MPEvents.FileLoaded, {
      modal: 'Scenario add modal',
      tags: ['SCENARIO'],
    });
    if (acceptedFiles && acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      setError('');
      setIsUploadSuccessful(false);
      setValidationErrors([]);
      parseCSVToJSON(selectedFile);
      setIsScvDirty(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
  });

  const validateRow = async (
    row: Record<string, any>,
    rowIndex: number,
  ): Promise<Record<string, any> | { rowIndex: number; errors: { path: string; message: string }[] }> => {
    const mappedSource = mapSourceToNumber(row.Sources);
    const mappedEvent = mapEventToNumber(row.Events);
    const mappedConsequence = mapConsequenceToNumber(row.Consequences);

    const adjustedRow = {
      ...row,
      Sources: mappedSource ?? null,
      Events: mappedEvent ?? null,
      Consequences: mappedConsequence ?? null,
      [ScenarioCsvHeaders.UnmodifiedFrequencyTimes]:
        row[ScenarioCsvHeaders.UnmodifiedFrequencyTimes] !== null
          ? row[ScenarioCsvHeaders.UnmodifiedFrequencyTimes].toString()
          : null,
      [ScenarioCsvHeaders.UnmodifiedFrequencyYears]:
        row[ScenarioCsvHeaders.UnmodifiedFrequencyYears] !== null
          ? row[ScenarioCsvHeaders.UnmodifiedFrequencyYears].toString()
          : null,
      [ScenarioCsvHeaders.UnmodifiedConsequenceLower]:
        row[ScenarioCsvHeaders.UnmodifiedConsequenceLower] !== null
          ? removeCommas(row[ScenarioCsvHeaders.UnmodifiedConsequenceLower])
          : null,
      [ScenarioCsvHeaders.UnmodifiedConsequenceUpper]:
        row[ScenarioCsvHeaders.UnmodifiedConsequenceUpper] !== null
          ? removeCommas(row[ScenarioCsvHeaders.UnmodifiedConsequenceUpper])
          : null,
    };

    try {
      await scenarioSchema.validate(adjustedRow, { abortEarly: false });
      return adjustedRow;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const customErrors = err.inner.map((e) => {
          if (e.path === 'Sources' && typeof mappedSource === 'string') {
            return { path: 'Sources', message: mappedSource };
          } else if (e.path === 'Events' && typeof mappedEvent === 'string') {
            return { path: 'Events', message: mappedEvent };
          } else if (e.path === 'Consequences' && typeof mappedConsequence === 'string') {
            return { path: 'Consequences', message: mappedConsequence };
          } else {
            return { path: e.path, message: e.message };
          }
        });
        return { rowIndex, errors: customErrors };
      } else {
        console.error('Unexpected error during validation:', err);
        return {
          rowIndex,
          errors: [{ path: 'Unexpected Error', message: 'An unexpected error occurred during validation.' }],
        };
      }
    }
  };

  const parseCSVToJSON = (file: File) => {
    const tryParse = (delimiter: string) =>
      new Promise<ParseResult>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          delimiter,
          complete: resolve,
          error: reject,
        });
      });

    (async () => {
      let result: ParseResult | null = null;

      for (const delim of delimiters) {
        const parsed = await tryParse(delim);
        if (parsed.data && Object.keys(parsed.data[0] || {}).length > 2) {
          result = parsed;
          break;
        }
      }

      if (!result || !result.data || result.data.length === 0) {
        setValidationErrors([
          { rowIndex: 0, errors: [{ path: 'Data', message: 'No scenario data found in the file.' }] },
        ]);
        return;
      }

      if (result.data.length > 60) {
        setError('The number of scenarios exceeds the limit of 60.');
        setFile(null);
        return;
      }

      let firstRow = result.data[0];
      const expectedHeaders = Object.values(ScenarioCsvHeaders).map(String);
      let parsedHeaders = firstRow ? Object.keys(firstRow) : [];

      if (!parsedHeaders) {
        result.data = result.data.slice(1);
      }

      result.data = result.data.map((row) => {
        const cleanRow = expectedHeaders.reduce((acc, key) => {
          acc[key as keyof ScenarioCSV] = row[key] ?? null;
          return acc;
        }, {} as ScenarioCSV);
        return cleanRow;
      });

      const validationPromises = result.data.map((row, index) => validateRow(row, index + 1));
      const validationResults = await Promise.all(validationPromises);

      const errorLog = validationResults.filter((res) => res && 'errors' in res) as ValidationErrorItem[];
      if (errorLog.length > 0) {
        setValidationErrors(errorLog);
        setIsUploadSuccessful(false);
        setReadyForUpload(false);
      } else {
        const filteredValidScenarios: ScenarioCSV[] = validationResults.filter(
          (res): res is ScenarioCSV => !('errors' in res),
        );
        setValidScenarios(filteredValidScenarios);
        setValidationErrors([]);
        setError('');
        setReadyForUpload(true);
      }
    })();
  };

  const saveScenarios = async (scenarios: ScenarioCSV[]) => {
    mpEvent(MPEvents.ButtonClick, {
      button: 'Upload',
      modal: 'Scenario add modal',
      tags: ['SCENARIO'],
    });

    const chunkedScenarios = chunkArray(scenarios, 30);
    setReadyForUpload(false);

    await upsertScenarios({
      variables: {
        assessmentId,
        scenariosData: chunkedScenarios.flatMap((chunk) => {
          return chunk.map((scenario) => {
            const { userAdd } = selectUserOption({
              usersOptios,
              selectedUser: { text: scenario[ScenarioCsvHeaders.RiskOwner] },
            });

            return {
              name: scenario[ScenarioCsvHeaders.Name],
              status: scenario[ScenarioCsvHeaders.Status],
              source: scenario[ScenarioCsvHeaders.Sources],
              event: scenario[ScenarioCsvHeaders.Events],
              consequence: scenario[ScenarioCsvHeaders.Consequences],
              riskOwner: userAdd,
              riskNarrative: scenario[ScenarioCsvHeaders.RiskNarrative],
              frequencyTimes: convertToNumber(scenario[ScenarioCsvHeaders.UnmodifiedFrequencyTimes]),
              frequencyYears: convertToNumber(scenario[ScenarioCsvHeaders.UnmodifiedFrequencyYears]),
              frequencyNote: scenario[ScenarioCsvHeaders.FrequencyNote],
              upperFormula: scenario[ScenarioCsvHeaders.UnmodifiedConsequenceUpper],
              lowerFormula: scenario[ScenarioCsvHeaders.UnmodifiedConsequenceLower],
              consequenceNote: scenario[ScenarioCsvHeaders.ConsequenceNote],
              managementStrategy: scenario[ScenarioCsvHeaders.RiskManagementStrategy],
              managementNote: scenario[ScenarioCsvHeaders.RiskManagementNote],
              reviewDate: scenario[ScenarioCsvHeaders.ReviewDate]
                ? (moment(scenario[ScenarioCsvHeaders.ReviewDate], 'DD-MM-YYYY').toISOString() as string)
                : undefined,
              riskIdentifiedDate: scenario[ScenarioCsvHeaders.IdentifiedDate]
                ? (moment(scenario[ScenarioCsvHeaders.IdentifiedDate], 'DD-MM-YYYY').toISOString() as string)
                : undefined,
            } satisfies ScenarioInput;
          });
        }),
      },
    });

    queryCache.invalidateQueries();
    refetchQuery({ include: ['assessment'] });
    setIsScvDirty(false);
    setIsUploadSuccessful(true);
    setReadyForUpload(false);
  };

  const resetUpload = () => {
    setIsUploadSuccessful(false);
    setValidationErrors([]);
    setError('');
    setFile(null);
    setReadyForUpload(false);
  };

  return (
    <DzoneWrap className="h-padding">
      <a
        href={csvTemplateLink}
        download="scenario_template.csv"
        style={{ textDecoration: 'none' }}
        onClick={() =>
          mpEvent(MPEvents.DownloadCSVTemplate, {
            modal: 'Scenario add modal',
            tags: ['SCENARIO'],
          })
        }
      >
        <GradientTextAction $underline weight={500}>
          Download CSV Template
        </GradientTextAction>
      </a>
      <Spacer $px={20} />
      {!isUploadSuccessful && (
        <Dropzone {...getRootProps()}>
          <input {...getInputProps()} />
          <Poppins px={14}>
            {isDragActive ? 'Drop the file here ...' : 'Drag and drop a file here, or click to select file'}
          </Poppins>
        </Dropzone>
      )}
      {!isUploadSuccessful && <Spacer $px={20} />}
      {file && (
        <>
          <Poppins px={14}>File selected: {file.name}</Poppins>
          <Spacer $px={15} />
        </>
      )}
      {file && validationErrors.length === 0 && (
        <>
          <Poppins px={14}>Scenarios ready for upload: {validScenarios.length}</Poppins>
          <Spacer $px={15} />
        </>
      )}
      {isUploadSuccessful && (
        <>
          <Poppins px={14}>File uploaded successfully!</Poppins>
          <Spacer $px={15} />
        </>
      )}
      {readyForUpload && !isUploadSuccessful && (
        <>
          <Poppins px={14}>Validation successful, ready for upload</Poppins>
          <Spacer $px={15} />
        </>
      )}
      {validationErrors.length > 0 && (
        <>
          <ErrorLog>
            {validationErrors.map((error, index) => (
              <ErrorItem key={index}>
                <Poppins color="error" px={16} weight={600}>
                  Error in row {error.rowIndex + 1}:
                </Poppins>
                {error.errors.map((e, idx) => (
                  <Poppins px={14} color="error" css="display: block" key={idx}>{`${e.path}: ${e.message}`}</Poppins>
                ))}
              </ErrorItem>
            ))}
          </ErrorLog>
          <Spacer $px={15} />
        </>
      )}
      {!isUploadSuccessful ? (
        <Button
          primary
          onClick={readyForUpload ? () => saveScenarios(validScenarios) : resetUpload}
          css="width: 100%;"
          disabled={!file || !!validationErrors.length || loading}
        >
          {readyForUpload || loading ? 'Upload' : 'Select File'}
        </Button>
      ) : (
        <>
          <Button
            primary
            onClick={() => {
              resetUpload();
              setFile(null);
            }}
            css="width: 100%;"
          >
            Upload Another File
          </Button>
          <Spacer $px={20} />
          <Button primary onClick={onClose} css="width: 100%;">
            Done
          </Button>
        </>
      )}
      <Spacer $px={30} />
      {!file && (
        <div>
          <Poppins px={14}>
            The valid values of Sources, Events and Consequences are listed in the 3 drop down lists below.
          </Poppins>
          <Poppins css="display: block" px={14}>
            Blank is also valid for each.
          </Poppins>
          <Spacer $px={24} />

          <div className="grid-list">
            <div>
              <InputLabel>Sources</InputLabel>
              <SourcesSelect placeholder="Sources" menuPlacement="top" />
            </div>
            <div>
              <InputLabel>Events</InputLabel>
              <EventsSelect placeholder="Events" menuPlacement="top" />
            </div>
            <div>
              <InputLabel>Consequences</InputLabel>
              <ConsequencesSelect placeholder="Consequences" menuPlacement="top" />
            </div>
          </div>
          <Spacer $px={20} />
        </div>
      )}
      {error && (
        <>
          <Spacer $px={20} />
          <div className="error">{error}</div>
        </>
      )}
    </DzoneWrap>
  );
};
